import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Swal from "sweetalert2";
import defaultProfile from "../../assets-super-admin/userImg.png";
import api from "../../configs/api";
import { formatDate } from "../../utils/helpers";

const AgentDetailsModal = ({ agent, onClose }) => {
  if (!agent) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>

        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Agent Details
        </h2>

        {/* Basic Info */}
        <div className="flex gap-4 items-center mb-6">
          <img
            src={agent.user.profileImage || defaultProfile}
            className="w-20 h-20 rounded-full object-cover border"
            alt="profile"
          />
          <div>
            <p className="text-lg font-medium">{agent.user.fullName}</p>
            <p className="text-sm text-gray-600">{agent.user.email}</p>
            <p className="text-sm text-gray-600">{agent.user.phone}</p>
            <p className="text-sm text-gray-600">
              City: {agent.user.preferredCity}
            </p>
            {agent.user.isVerified && (
              <span className="text-green-600 text-sm font-medium">
                ✔ Verified
              </span>
            )}
          </div>
        </div>

        {/* Business Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700">RERA ID</p>
            <p>{agent.reraId || "N/A"}</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Operating Since</p>
            <p>{agent.operatingSince || "N/A"}</p>
          </div>

          <div>
            <p className="font-medium text-gray-700">Operating Areas</p>
            <p>{(agent.operatingAreas || []).join(", ") || "N/A"}</p>
          </div>

          <div>
            <p className="font-medium text-gray-700">Deal Types</p>
            <p>{(agent.dealTypes || []).join(", ") || "N/A"}</p>
          </div>

          <div>
            <p className="font-medium text-gray-700">Properties For Sale</p>
            <p>{agent.propertiesForSale ?? 0}</p>
          </div>

          <div>
            <p className="font-medium text-gray-700">Deals Closed</p>
            <p>{agent.dealClosed ?? 0}</p>
          </div>

          <div>
            <p className="font-medium text-gray-700">Team Members</p>
            <p>{agent.teamMembers ?? 0}</p>
          </div>
        </div>

        {/* Company Info */}
        {agent.company && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Company Information
            </h3>
            <div className="flex items-center gap-3">
              {agent.company.companyLogo ? (
                <img
                  src={agent.company.companyLogo}
                  className="w-14 h-14 rounded border object-cover"
                  alt=""
                />
              ) : (
                <div className="w-14 h-14 rounded border flex items-center justify-center text-sm text-gray-500">
                  Logo
                </div>
              )}
              <div>
                <p className="font-medium">{agent.company.companyName}</p>
                <p className="text-sm text-gray-600">
                  RERA: {agent.company.reraId || "N/A"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

const SuperAdminAgentBuilderList = () => {
  const [agents, setAgents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        showCalendar
      ) {
        setShowCalendar(false);
      }
    };

    const handleScrollClose = () => {
      if (showCalendar) setShowCalendar(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollClose, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollClose, true);
    };
  }, [showCalendar]);

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await api.get("/agent-builder/all");
        const list = res.data.list || [];
        setAgents(list);
        setFiltered(list);
      } catch (err) {
        console.error("Error loading agents:", err);
      }
    };
    fetchAgents();
  }, []);

  useEffect(() => {
    const s = search.trim().toLowerCase();

    let result = agents.filter((a) => {
      const hay = `${a.user.fullName || ""} ${a.user.email || ""} ${
        a.user.phone || ""
      } ${a.user.preferredCity || ""} ${
        a.company?.companyName || ""
      }`.toLowerCase();
      return hay.includes(s);
    });

    // date filter if selectedDate set
    if (selectedDate) {
      const selDay = selectedDate.getDate();
      const selMonth = selectedDate.getMonth();
      const selYear = selectedDate.getFullYear();

      result = result.filter((a) => {
        if (!a.createdAt) return false;
        const d = new Date(a.createdAt);
        return (
          d.getDate() === selDay &&
          d.getMonth() === selMonth &&
          d.getFullYear() === selYear
        );
      });
    }

    setFiltered(result);
  }, [search, selectedDate, agents]);

  const tableHeaders = [
    "No",
    "Lister Name",
    "Email ID",
    "Phone",
    "City",
    "Company",
    "RERA ID",
    "Joined",
    "Action",
  ];

  const MONTH_NAMES = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  const DAY_NAMES = useMemo(
    () => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    []
  );

  const handleDeleteAgent = async (agentId) => {
    const confirm = await Swal.fire({
      title: "Delete Agent?",
      text: "This action cannot be undone.",
      icon: "warning",

      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",

      confirmButtonColor: "#FF4523",
      cancelButtonColor: "#735DFF",

      background: "#FFFFFF",
      color: "#000750",
      buttonsStyling: false,
      customClass: {
        popup: "rounded-xl shadow-xl border border-gray-200",
        title: "text-lg font-semibold text-text-primary",
        htmlContainer: "text-text-secondary",
        confirmButton:
          "px-5 py-2 rounded-lg font-medium bg-status-danger text-white hover:bg-red-600 transition-all",
        cancelButton:
          "px-5 py-2 rounded-lg font-medium bg-primary text-white hover:bg-primary/90 transition-all ml-2",
      },
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.delete(`/agent-builder/${agentId}`);

      setAgents((prev) => prev.filter((a) => a._id !== agentId));
      setFiltered((prev) => prev.filter((a) => a._id !== agentId));

      Swal.fire({
        title: "Deleted!",
        text: "Agent removed successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
        background: "#FFFFFF",
        color: "#000750",
        iconColor: "#0CC763",
        customClass: {
          popup: "rounded-xl shadow-lg border border-gray-200",
          title: "text-lg font-semibold",
        },
      });

      setDropdownOpen(null);
    } catch (err) {
      console.error("Delete error:", err);

      Swal.fire({
        title: "Error",
        text: "Failed to delete agent. Try again later.",
        icon: "error",

        confirmButtonText: "Okay",
        confirmButtonColor: "#735DFF",
        background: "#FFFFFF",
        iconColor: "#FF4523",
        customClass: {
          popup: "rounded-xl shadow-lg border border-gray-200",
          title: "text-lg font-semibold text-status-danger",
        },
      });
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      daysInMonth: lastDay.getDate(),
      startingDayOfWeek: firstDay.getDay(),
    };
  };

  const changeCalendarMonth = (increment) => {
    setCalendarViewDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + increment, 1)
    );
  };

  const handleSelectDate = (day) => {
    const chosen = new Date(
      calendarViewDate.getFullYear(),
      calendarViewDate.getMonth(),
      day
    );
    setSelectedDate(chosen);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setShowCalendar(false);
  };

  const CalendarDropdown = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(calendarViewDate);
    const today = new Date();

    return (
      <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-80">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => changeCalendarMonth(-1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <span className="text-sm font-semibold text-gray-800">
            {MONTH_NAMES[calendarViewDate.getMonth()]}{" "}
            {calendarViewDate.getFullYear()}
          </span>
          <button
            onClick={() => changeCalendarMonth(1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAY_NAMES.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500 py-1"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {[...Array(startingDayOfWeek)].map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {[...Array(daysInMonth)].map((_, i) => {
            const day = i + 1;
            const isSelected =
              selectedDate &&
              day === selectedDate.getDate() &&
              calendarViewDate.getMonth() === selectedDate.getMonth() &&
              calendarViewDate.getFullYear() === selectedDate.getFullYear();

            const isToday =
              day === today.getDate() &&
              calendarViewDate.getMonth() === today.getMonth() &&
              calendarViewDate.getFullYear() === today.getFullYear();

            return (
              <button
                key={day}
                onClick={() => handleSelectDate(day)}
                className={`aspect-square flex items-center justify-center text-sm rounded-lg hover:bg-gray-100
                  ${
                    isSelected ? "bg-blue-500 text-white hover:bg-blue-600" : ""
                  }
                  ${
                    isToday && !isSelected
                      ? "border border-blue-500 text-blue-500"
                      : "text-gray-700"
                  }
                `}
              >
                {day}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex gap-2 justify-between">
          <button
            onClick={clearDateFilter}
            className="text-sm text-gray-600 hover:underline"
          >
            Clear
          </button>
          <button
            onClick={() => setShowCalendar(false)}
            className="text-sm text-gray-600 hover:underline"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 border rounded-lg shadow-sm">
      {/* Header */}
      <h1 className="text-[#212B36] rounded-t-lg border-b-2 bg-[#F9F9F9] px-4 py-6 font-dm-sans font-semibold text-[16px] capitalize">
        Agent / Builder List
      </h1>

      {/* Filters Section */}
      <div className="bg-[#F9F9F9] shadow-sm p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search Agents"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* CLEAR ALL BUTTON */}
          {(search || selectedDate) && (
            <button
              onClick={() => {
                setSearch("");
                setSelectedDate(null);
              }}
              className="px-4 py-2 text-sm rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              Clear All
            </button>
          )}

          {/* DATE FILTER BUTTON */}
          <div className="relative">
            <button
              onClick={() => {
                setShowCalendar((s) => !s);
                setCalendarViewDate(selectedDate || new Date());
              }}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            >
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-[#515B73] text-sm">
                {selectedDate ? selectedDate.toLocaleDateString() : "Date"}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-600" />
            </button>
            {showCalendar && (
              <div ref={calendarRef}>
                <CalendarDropdown />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#E9EDF4]">
              <tr>
                {tableHeaders.map((h) => (
                  <th
                    key={h}
                    className="py-3 px-2 text-left text-gray-600 font-medium text-sm"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((agent, index) => (
                <tr key={agent._id} className="hover:bg-gray-50 relative">
                  <td className="px-2 text-sm py-4">
                    {String(index + 1).padStart(2, "0")}
                  </td>
                  <td className="px-2 text-sm">{agent.user.fullName}</td>
                  <td className="px-2 text-sm">{agent.user.email || "—"}</td>
                  <td className="px-2 text-sm">{agent.user.phone || "—"}</td>
                  <td className="px-2 text-sm">
                    {agent.user.preferredCity || "—"}
                  </td>
                  <td className="px-2 text-sm">
                    {agent.company?.companyName || "—"}
                  </td>
                  <td className="px-2 text-sm">{agent.reraId || "—"}</td>
                  <td className="px-2 text-sm">
                    {formatDate(agent.createdAt)}
                  </td>

                  {/* ACTION BUTTON */}
                  <td className="px-2 relative">
                    <button
                      onClick={() =>
                        setDropdownOpen(
                          dropdownOpen === agent._id ? null : agent._id
                        )
                      }
                    >
                      <MoreVertical className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                    </button>

                    {/* Dropdown */}
                    {dropdownOpen === agent._id && (
                      <div
                        ref={dropdownRef}
                        className="absolute right-2 top-10 bg-white border shadow-lg rounded-md py-2 w-44 z-50"
                      >
                        <button
                          onClick={() => {
                            setSelectedAgent(agent);
                            setDropdownOpen(null);
                          }}
                          className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                        >
                          View Details
                        </button>

                        <button
                          onClick={() => handleDeleteAgent(agent._id)}
                          className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 text-sm"
                        >
                          Delete Agent
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={tableHeaders.length + 1}
                    className="p-6 text-center text-sm text-gray-600"
                  >
                    No agents found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <AgentDetailsModal
        agent={selectedAgent}
        onClose={() => setSelectedAgent(null)}
      />
    </div>
  );
};

export default SuperAdminAgentBuilderList;

// import React, { useState } from "react";
// import {
//   Search,
//   Calendar,
//   ChevronDown,
//   MoreVertical,
//   ChevronLeft,
//   ChevronRight,
// } from "lucide-react";

// const SuperAdminAgentBuilderList = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [selectedDate, setSelectedDate] = useState(new Date(2025, 3, 22));
//   const [showCalendar, setShowCalendar] = useState(false);

//   const MONTH_NAMES = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
//   ];
//   const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

//   const agents = [
//     {
//       id: 1,
//       name: "Satish Sharma",
//       email: "satishsharma@gmail.com",
//       propertyType: "Commercial",
//       date: "22-04-2025",
//       number: "+91 99567 89765",
//       property: "1 RK commercial Property",
//     },
//     {
//       id: 2,
//       name: "Aman Gupta",
//       email: "amang@gmail.com",
//       propertyType: "Residential",
//       date: "23-04-2025",
//       number: "+91 97865 45321",
//       property: "2 BHK Apartment",
//     },
//     {
//       id: 3,
//       name: "Rohit Mehta",
//       email: "rohitm@gmail.com",
//       propertyType: "Plot",
//       date: "25-04-2025",
//       number: "+91 88990 77665",
//       property: "Green Valley Plot",
//     },
//   ];

//   const tableHeaders = [
//     "No",
//     "Lister Name",
//     "Email ID",
//     "Property Type",
//     "Listing Date",
//     "Number",
//     "Property Name",
//     "Action",
//   ];

//   const formatDate = (date) => {
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = date.getFullYear();
//     return `${day}/${month}/${year}`;
//   };

//   const getDaysInMonth = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth();
//     const firstDay = new Date(year, month, 1);
//     const lastDay = new Date(year, month + 1, 0);
//     return {
//       daysInMonth: lastDay.getDate(),
//       startingDayOfWeek: firstDay.getDay(),
//     };
//   };

//   const changeMonth = (increment) => {
//     setSelectedDate(
//       new Date(
//         selectedDate.getFullYear(),
//         selectedDate.getMonth() + increment,
//         1
//       )
//     );
//   };

//   const selectDate = (day) => {
//     setSelectedDate(
//       new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day)
//     );
//     setShowCalendar(false);
//   };

//   const CalendarDropdown = () => {
//     const { daysInMonth, startingDayOfWeek } = getDaysInMonth(selectedDate);
//     const today = new Date();

//     return (
//       <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-80">
//         <div className="flex items-center justify-between mb-4">
//           <button
//             onClick={() => changeMonth(-1)}
//             className="p-1 hover:bg-gray-100 rounded"
//           >
//             <ChevronLeft className="w-5 h-5 text-gray-600" />
//           </button>
//           <span className="text-sm font-semibold text-gray-800">
//             {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getFullYear()}
//           </span>
//           <button
//             onClick={() => changeMonth(1)}
//             className="p-1 hover:bg-gray-100 rounded"
//           >
//             <ChevronRight className="w-5 h-5 text-gray-600" />
//           </button>
//         </div>

//         <div className="grid grid-cols-7 gap-1 mb-2">
//           {DAY_NAMES.map((day) => (
//             <div
//               key={day}
//               className="text-center text-xs font-medium text-gray-500 py-1"
//             >
//               {day}
//             </div>
//           ))}
//         </div>

//         <div className="grid grid-cols-7 gap-1">
//           {[...Array(startingDayOfWeek)].map((_, i) => (
//             <div key={`empty-${i}`} className="aspect-square" />
//           ))}

//           {[...Array(daysInMonth)].map((_, i) => {
//             const day = i + 1;
//             const isSelected = day === selectedDate.getDate();
//             const isToday =
//               day === today.getDate() &&
//               selectedDate.getMonth() === today.getMonth() &&
//               selectedDate.getFullYear() === today.getFullYear();

//             return (
//               <button
//                 key={day}
//                 onClick={() => selectDate(day)}
//                 className={`aspect-square flex items-center justify-center text-sm rounded-lg hover:bg-gray-100
//                   ${
//                     isSelected ? "bg-blue-500 text-white hover:bg-blue-600" : ""
//                   }
//                   ${
//                     isToday && !isSelected
//                       ? "border border-blue-500 text-blue-500"
//                       : "text-gray-700"
//                   }
//                 `}
//               >
//                 {day}
//               </button>
//             );
//           })}
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="bg-gray-50 border rounded-lg shadow-sm">
//       <div>
//         <h1 className="text-[#212B36] rounded-t-lg border-b-2 bg-[#F9F9F9] px-4 py-6 font-dm-sans font-semibold text-[16px] capitalize">
//           Agent / Builder List
//         </h1>
//       </div>

//       {/* Filters Section */}
//       <div className="bg-[#F9F9F9] shadow-sm p-4">
//         <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
//           <div className="relative flex-1 max-w-md">
//             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
//             <input
//               type="text"
//               placeholder="Search"
//               className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//             />
//           </div>

//           <div className="flex items-center gap-3 flex-wrap">
//             <div className="relative">
//               <button
//                 onClick={() => setShowCalendar(!showCalendar)}
//                 className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
//               >
//                 <Calendar className="w-4 h-4 text-gray-600" />
//                 <span className="text-[#515B73] text-sm">
//                   {formatDate(selectedDate)}
//                 </span>
//               </button>
//               {showCalendar && <CalendarDropdown />}
//             </div>

//             <div className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50 transition-colors">
//               <span className="text-[#515B73] text-sm">By Property Type</span>
//               <ChevronDown className="w-4 h-4 text-gray-600" />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Table Section */}
//       <div className="bg-white shadow-sm overflow-hidden">
//         <div className="overflow-x-auto">
//           <table className="w-full">
//             <thead className="bg-[#E9EDF4]">
//               <tr>
//                 <th className="px-4">
//                   <input type="checkbox" className="rounded border-gray-300" />
//                 </th>
//                 {tableHeaders.map((header) => (
//                   <th
//                     key={header}
//                     className="py-3 px-2 text-left text-gray-600 font-medium text-sm whitespace-nowrap"
//                   >
//                     {header}
//                   </th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-200">
//               {agents.map((agent, index) => (
//                 <tr key={agent.id} className="hover:bg-gray-50">
//                   <td className="px-4 py-4">
//                     <input
//                       type="checkbox"
//                       className="rounded border-gray-300"
//                     />
//                   </td>
//                   <td className="px-2 text-sm text-gray-800">
//                     {String(index + 1).padStart(2, "0")}
//                   </td>
//                   <td className="px-2 text-sm text-gray-800">{agent.name}</td>
//                   <td className="px-2 text-sm text-gray-800">{agent.email}</td>
//                   <td className="px-2 text-sm text-gray-800">
//                     {agent.propertyType}
//                   </td>
//                   <td className="px-2 text-sm text-gray-800">{agent.date}</td>
//                   <td className="px-2 text-sm text-gray-800">{agent.number}</td>
//                   <td className="px-2 text-sm text-gray-800">
//                     {agent.property}
//                   </td>
//                   <td className="px-2">
//                     <button className="text-gray-400 hover:text-gray-600">
//                       <MoreVertical className="w-5 h-5" />
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>
//       {/* Pagination */}
//       <div className="flex flex-col sm:flex-row items-center justify-between mt-10 px-6 py-4 border-t border-gray-200 gap-4">
//         <div className="text-sm text-gray-600">Showing 1-8 of 100</div>
//         <div className="flex items-center gap-2">
//           <button
//             onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
//             className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
//           >
//             Prev
//           </button>
//           {[1, 2].map((page) => (
//             <button
//               key={page}
//               className={`px-3 py-1 text-sm rounded ${
//                 currentPage === page
//                   ? "bg-blue-500 text-white"
//                   : "text-gray-600 hover:bg-gray-100"
//               }`}
//               onClick={() => setCurrentPage(page)}
//             >
//               {page}
//             </button>
//           ))}
//           <button
//             onClick={() => setCurrentPage(Math.min(13, currentPage + 1))}
//             className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
//           >
//             Next
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SuperAdminAgentBuilderList;
